/**
 * Seed content for the Release Notes & Roadmap page.
 *
 * This is intentionally the ONLY place page content lives so that, when the
 * backend is wired up, `releaseNotesAPI.getReleaseNotes()` can swap this static
 * import for a network response of the SAME shape and nothing else changes.
 *
 * Shapes (also the expected backend contract):
 *
 *   Release {
 *     id:         string   // human-facing release id, e.g. "v1.4.0"
 *     date:       string   // ISO date "YYYY-MM-DD"
 *     title:      string
 *     highlights: string[] // "What's New" bullet points
 *     tags?:      string[] // optional area chips (Insights, Analytics, ...)
 *   }
 *
 *   BacklogItem {
 *     id:          string  // stable slug — used to key comments
 *     title:       string
 *     description: string
 *     status:      "In Progress" | "Planned" | "Exploring"
 *     eta?:        string  // free text, e.g. "Aug 2026"
 *   }
 *
 * Every release also carries a stable `id` used to key its comment thread.
 */

export const RELEASE_NOTES_SEED = {
  releases: [
    {
      id: "v1.4.0",
      date: "2026-07-10",
      title: "Insights Details — Simplification",
      tags: ["Insights", "UI"],
      highlights: [
        "Streamlined the Insights Details page to surface the sections that matter most: Evaluation Score Summary, Customer Profile, Agent Scoring, and Call Outcome.",
        "Sections now render in a consistent, colour-coded tabular layout with clear evidence and confidence columns.",
        "Score badges are colour-banded (green ≥ 75, amber 50–74, red < 50) for faster reading.",
        "Empty and not-applicable fields are automatically hidden in the page view (still retained in the exported PDF).",
      ],
    },
    {
      id: "v1.3.0",
      date: "2026-06-18",
      title: "Montenido Usage Reporting",
      tags: ["Analytics"],
      highlights: [
        "Added a Montenido Usage report showing all @montenido.com users sorted by most recent activity.",
        "One-click CSV export of the usage table.",
        "30-day rolling view of endpoint hits and unique endpoints per user.",
      ],
    },
    {
      id: "v1.2.0",
      date: "2026-05-22",
      title: "Agent Feedback & Notifications",
      tags: ["Agent", "Notifications"],
      highlights: [
        "Real-time notification bell for feedback replies and assigned issues.",
        "Agent feedback modal reachable directly from a notification.",
        "Deep links from notifications into the relevant call details.",
      ],
    },
  ],

  upcoming: [
    {
      id: "bk-comment-sharing",
      title: "Shared comments on releases & backlog",
      description:
        "Let Monte Nido and Magic Health leave comments on any release or backlog item and choose who each comment is shared with, so priorities can be redirected in-context.",
      status: "In Progress",
      eta: "Jul 2026",
    },
    {
      id: "bk-agent-coaching",
      title: "Inline agent coaching hints",
      description:
        "Surface targeted, per-call coaching suggestions for agents based on the scoring breakdown, directly within the call details view.",
      status: "Planned",
      eta: "Aug 2026",
    },
    {
      id: "bk-trend-dashboard",
      title: "Backlog progression dashboard",
      description:
        "A subtle trend view of backlog items moving from Exploring → Planned → In Progress → Shipped, making the pace of delivery visible at a glance.",
      status: "Exploring",
      eta: "Q3 2026",
    },
  ],
};
